import {
  Box,
  Button,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  useTheme,
} from '@material-ui/core'
import { LinkRounded, ShareRounded } from '@material-ui/icons'
import { useRef, useState } from 'react'
import {
  EmailShareButton,
  EmailIcon,
  FacebookShareButton,
  FacebookIcon,
  PinterestShareButton,
  PinterestIcon,
  RedditShareButton,
  RedditIcon,
  TelegramShareButton,
  TelegramIcon,
  TumblrShareButton,
  TumblrIcon,
  TwitterShareButton,
  TwitterIcon,
  WhatsappShareButton,
  WhatsappIcon,
} from 'react-share'
import { formatUrl } from 'url-lib'
import useCopyUrl from './useCopyUrl'
import { site } from '../config/main.json'

const DEFAULT_ICON_SIZE = 48

interface IconsListProps {
  imageUrl: string
  shortSummary: string
  summary: string
  title: string
  url: string
}

const IconsList = ({
  imageUrl,
  shortSummary,
  summary,
  title,
  url,
}: IconsListProps) => {
  const { spacing } = useTheme()
  const socialUrl = formatUrl(url, {
    utm_medium: 'social',
    utm_campaign: 'share',
  })

  return (
    <Box
      display="grid"
      gridTemplateColumns="repeat(4, 1fr)"
      gridColumnGap={spacing(1)}
    >
      <TwitterShareButton
        url={formatUrl(socialUrl, { utm_source: 'twitter' })}
        title={`#NBAPlayerTiers\n\n${shortSummary}\n\n`}
        related={[site.owner.twitter]}
        aria-label="Share on Twitter"
      >
        <TwitterIcon round size={DEFAULT_ICON_SIZE} />
      </TwitterShareButton>

      <FacebookShareButton
        url={formatUrl(socialUrl, { utm_source: 'facebook' })}
        quote={`${title}\n\n${summary}`}
        aria-label="Share on Facebook"
      >
        <FacebookIcon round size={DEFAULT_ICON_SIZE} />
      </FacebookShareButton>

      <WhatsappShareButton
        url={formatUrl(socialUrl, { utm_source: 'whatsapp' })}
        title={`${title} | NBA Player Tiers`}
        aria-label="Share on Whatsapp"
      >
        <WhatsappIcon round size={DEFAULT_ICON_SIZE} />
      </WhatsappShareButton>

      <RedditShareButton
        url={formatUrl(socialUrl, { utm_source: 'reddit' })}
        title={`${title} | NBA Player Tiers`}
        aria-label="Share on Reddit"
      >
        <RedditIcon round size={DEFAULT_ICON_SIZE} />
      </RedditShareButton>

      <TumblrShareButton
        url={formatUrl(socialUrl, { utm_source: 'tumblr' })}
        title={title}
        tags={['NBAPlayerTiers']}
        caption={shortSummary}
        aria-label="Share with Tumblr"
      >
        <TumblrIcon round size={DEFAULT_ICON_SIZE} />
      </TumblrShareButton>

      <PinterestShareButton
        url={formatUrl(socialUrl, { utm_source: 'pinterest' })}
        media={imageUrl}
        description={`#NBAPlayerTiers\n\n${shortSummary}\n\n`}
        aria-label="Share on Pinterest"
      >
        <PinterestIcon round size={DEFAULT_ICON_SIZE} />
      </PinterestShareButton>

      <TelegramShareButton
        url={formatUrl(socialUrl, { utm_source: 'telegram' })}
        title={`${title} | NBA Player Tiers`}
        aria-label="Share on Telegram"
      >
        <TelegramIcon round size={DEFAULT_ICON_SIZE} />
      </TelegramShareButton>

      <EmailShareButton
        url={formatUrl(socialUrl, { utm_source: 'email' })}
        subject={`${title} | NBA Player Tiers`}
        body={summary}
        separator={`

`}
        aria-label="Share with Email"
      >
        <EmailIcon round size={DEFAULT_ICON_SIZE} />
      </EmailShareButton>
    </Box>
  )
}

interface Props extends IconsListProps {}

const Share = ({ imageUrl, shortSummary, summary, title, url }: Props) => {
  const [copyUrlStatus, copyUrl] = useCopyUrl(url)
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)

  return (
    <>
      <Button
        color="primary"
        variant="contained"
        startIcon={<ShareRounded />}
        aria-controls={open ? 'share-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="menu"
        onClick={() => setOpen((curOpen) => !curOpen)}
        ref={anchorRef}
      >
        Share
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener
                onClickAway={(e) => {
                  if (anchorRef.current?.contains(e.target as HTMLElement)) {
                    return
                  }

                  setOpen(false)
                }}
              >
                <Box maxWidth="250px" p={2}>
                  <IconsList
                    imageUrl={imageUrl}
                    title={title}
                    url={url}
                    shortSummary={shortSummary}
                    summary={summary}
                  />
                  <Box mt={1}>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<LinkRounded />}
                      onClick={copyUrl}
                    >
                      {copyUrlStatus === 'copied'
                        ? 'Copied'
                        : copyUrlStatus === 'failed'
                        ? 'Copy failed!'
                        : 'Copy URL'}
                    </Button>
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}

export default Share
